"use client";
import PropTypes from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import fetchWithRefresh from "../utils/fetchWithRefresh";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(["auth_session", "refresh_token"]);
  const [user, setFullUser] = useState(() => {
    try {
      const stored = localStorage.getItem("sg_user");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [selectedVenue, setSelectedVenue] = useState(() => cookies?.selectedVenue || null);
  const [selectedVenueData, setSelectedVenueData] = useState(() => cookies?.selectedVenueData || null);
  // true while the initial fetchVenueDetails is in-flight after a page refresh
  const [venueDataLoading, setVenueDataLoading] = useState(() => !!(cookies?.selectedVenue));
  let selectedVenueFormatted = null;

  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return !!cookies?.auth_session;
  });

  const logoutUser = async () => {
    try {
      const req = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Users/LogoutPortal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });
    } catch (error) {
      // LogErrors(user, logout, updateTokens, error);
    }
  };

  const fetchVenueDetails = async () => {
    try {
      const req = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Portal/FetchVenue?venueId=${selectedVenue?.value}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      }, logout, updateTokens, (message) => { toast.info(message); });

      if (req.status === 200) {
        const data = await req.json();
        setSelectedVenueData(data);
      } else {
          // LogErrors(user, logout, updateTokens, "User Profile Picture failed to update");
      }
    } catch (error) {
      // LogErrors(user, logout, updateTokens, error);
    } finally {
      setVenueDataLoading(false);
    }
  };

  useEffect(() => {
    setIsLoggedIn(!!cookies?.auth_session);
  }, [cookies.auth_session]);

  useEffect(() => {
    if(selectedVenue){
      console.log(selectedVenue);
      setVenueDataLoading(true);
      setCookie("selectedVenue", selectedVenue, { path: "/", secure: true, sameSite: "strict" });
      fetchVenueDetails();
    }
  }, [selectedVenue]);

  const login = (user, checked, email) => {
    setFullUser(user);
    localStorage.setItem("sg_user", JSON.stringify(user));
    setCookie("auth_session", user.token, { path: "/", secure: true, sameSite: "strict" });
    setCookie("refresh_token", user.refreshToken, { path: "/", secure: true, sameSite: "strict" });
    
    if(checked){
      localStorage.setItem("user_email", email);
    }

    if(user?.venues.length > 0 && !user?.isSuperAdmin){
      selectedVenueFormatted = {
        label: user?.venues[0].directoryDisplayName,
        value: user?.venues[0].organisationId
      }

      setSelectedVenue(selectedVenueFormatted);
      setCookie("selectedVenue", selectedVenueFormatted, { path: "/", secure: true, sameSite: "strict" });
    }

    setIsLoggedIn(true);
  };

  const updateTokens = ({ id, token, refreshToken, isSuperAdmin, isVenueAdmin, venues }) => {
    const updatedUser = {
      id,
      token,
      refreshToken,
      isSuperAdmin: Boolean(isSuperAdmin),
      isVenueAdmin: Boolean(isVenueAdmin),
      venues: Array.isArray(venues) ? venues : []
    };

    setFullUser(updatedUser);
    localStorage.setItem("sg_user", JSON.stringify(updatedUser));

    setCookie("auth_session", token, { path: "/", secure: true, sameSite: "strict" });
    setCookie("refresh_token", refreshToken, { path: "/", secure: true, sameSite: "strict" });
  };

  const logout = (redirect = true) => {
    removeCookie("auth_session", { path: "/" });
    removeCookie("refresh_token", { path: "/" });
    removeCookie("selectedVenue", { path: "/" });
    localStorage.removeItem("user_email");
    localStorage.removeItem("sg_user");
    setFullUser(null);
    setIsLoggedIn(false);
    setSelectedVenue(null);
    logoutUser();

    if (redirect) {
      window.location.href = "/auth/login";
    }
  };

  return (
    <UserContext.Provider value={{ user, login, logout, updateTokens, isLoggedIn, setIsLoggedIn, selectedVenue, setSelectedVenue, selectedVenueData, fetchVenueDetails, venueDataLoading }}>
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};